import { useState, useCallback } from 'react';

/**
 * Global salary-visibility preference.
 *
 * Persists across pages + sessions via localStorage under `hrms.showSalary`.
 * Used by DashboardPage, MyPayslipsPage, and any future page rendering
 * salary/CTC amounts that should be hideable during screen-sharing.
 *
 * Usage:
 *     const { showSalary, toggleSalary, mask } = useSalaryVisibility();
 *     <p>{mask(formatCurrency(amount))}</p>
 *     <Button onClick={toggleSalary}>{showSalary ? <EyeOff/> : <Eye/>}</Button>
 */
export default function useSalaryVisibility() {
  const [showSalary, setShowSalary] = useState(() => {
    try { return localStorage.getItem('hrms.showSalary') === 'true'; } catch { return false; }
  });

  const toggleSalary = useCallback(() => {
    setShowSalary(prev => {
      const next = !prev;
      try { localStorage.setItem('hrms.showSalary', String(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  /** Returns the given amount string when visible, or a branded mask when hidden. */
  const mask = useCallback((value) => (showSalary ? value : 'w i s e w i t'), [showSalary]);

  return { showSalary, toggleSalary, mask };
}
